import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Home, LayoutGrid, ShoppingCart, User } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { useLanguage } from '../contexts/LanguageContext';

/**
 * The tab bar is 3.5rem tall plus the safe-area inset. Anything that has to
 * clear it or sit directly on top of it uses these classes so the height is
 * only defined once. The nav below must be sized with the same expression:
 * `box-sizing: border-box` means a plain `h-14` would swallow the inset padding
 * instead of growing, so a bar using MOBILE_NAV_OFFSET would float above the
 * tabs by the inset amount.
 */
export const MOBILE_NAV_SPACER = 'pb-[calc(3.5rem_+_env(safe-area-inset-bottom))] lg:pb-0';
export const MOBILE_NAV_OFFSET = 'bottom-[calc(3.5rem_+_env(safe-area-inset-bottom))]';

/** The tabs on their own, for pages that stack their own bar on top. */
export const MobileNavTabs: React.FC = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const { cart } = useCart();
  const { pathname } = useLocation();
  const cartItemCount = cart?.items?.reduce((sum, item) => sum + (item.quantity || 0), 0) || 0;

  const tabs = [
    { to: '/', icon: Home, label: t.navbar.home, badge: 0 },
    { to: '/products', icon: LayoutGrid, label: t.navbar.products, badge: 0 },
    { to: '/cart', icon: ShoppingCart, label: t.navbar.cart, badge: cartItemCount },
    {
      to: user ? '/profile' : '/login',
      icon: User,
      label: user ? t.navbar.profile : t.navbar.signIn,
      badge: 0,
    },
  ];

  return (
    <nav className="grid h-[calc(3.5rem_+_env(safe-area-inset-bottom))] grid-cols-4 border-t border-gray-200 bg-white pb-[env(safe-area-inset-bottom)]">
      {tabs.map(({ to, icon: Icon, label, badge }) => {
        // Product pages live under /products, so that tab stays lit while browsing.
        const isActive = to === '/' ? pathname === '/' : pathname.startsWith(to);
        return (
          <Link
            key={to}
            to={to}
            aria-current={isActive ? 'page' : undefined}
            className={`flex flex-col items-center justify-center gap-1 text-[11px] font-medium transition-colors active:text-trend-clay ${
              isActive ? 'text-trend-clay' : 'text-trend-ink-muted'
            }`}
          >
            <span className="relative">
              <Icon className="h-5 w-5" />
              {badge > 0 && (
                <span className="absolute -right-2 -top-1.5 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-trend-clay px-1 text-[10px] font-bold text-white">
                  {badge > 99 ? '99+' : badge}
                </span>
              )}
            </span>
            {label}
          </Link>
        );
      })}
    </nav>
  );
};

/** App-style bottom navigation, pinned to the viewport below the lg breakpoint. */
const MobileBottomNav: React.FC = () => (
  <div className="fixed bottom-0 left-0 right-0 z-50 lg:hidden">
    <MobileNavTabs />
  </div>
);

export default MobileBottomNav;
